// Third party
import {
  GridCellParams,
  GridColDef,
  DataGridPro,
  GridFetchRowsParams,
  useGridApiRef,
  GridApiPro,
  GridSortModel,
} from '@mui/x-data-grid-pro';
import Typography from '@mui/material/Typography';
import startCase from 'lodash/startCase';
import get from 'lodash/get';
import SvgIcon from '@mui/material/SvgIcon';
import Box from '@mui/material/Box';
import LinearProgress from '@mui/material/LinearProgress';

// Hooks
import { useTranslation } from 'react-i18next';
import { useTheme } from '@mui/material';
import {
  useCallback, useMemo, useRef, memo, MutableRefObject, useState, useEffect,
} from 'react';
import Card from '@mui/material/Card';
import { unstable_debounce as debounce } from '@mui/utils';
import { GridFilterModel } from '@mui/x-data-grid/models/gridFilterModel';
import { TCustomTheme } from '@mgm/common/theme';
import { useCustomTheme } from '@mgm/common/hooks/useCustomTheme';
import { useScreenResize } from '@mgm/common/hooks/useResize';
import { MemoizedBaseAvatar, BaseLabel } from '@mgm/common/components/base';
import { CustomNoRowsOverlay } from '@mgm/common/components';
import { useAppDispatch, useAppSelector } from '../../../../hooks/useAppStore';
import { useTeamFilters, useTeamPermissions } from '../../hooks';

import {
  MemoizedFilterTabs,
  BaseChip,
} from '../../../../components/base';
import { MemberStatus } from './MemberStatus';
import { TableActions } from '../../../../components/table/TableActions';
import { MemoizedMultiLocations } from './MultiLocations';

// Redux-toolkit
import { memberActions } from '../../../../store/team/slice';
import { commonActions } from '../../../../store/common/slice';
import { getAuthenticateUser } from '../../../../store/authenticate/selectors';

// Assets
import { ReactComponent as ViewIcon } from '../../../../static/icons/ic_view.svg';

// Types
import { TCommissionPlan, TLocation, TMember } from '../../../../types';
import { useTable } from '../../../../hooks/useTable';
import { useCommon, usePermissions } from '../../../../hooks/useCommon';
import { getIsPrintMode } from '../../../../store/common/selectors';
import { PrintWrapper } from '../../../../layout/wrapper/PrintWrapper';
import { useGetTeamAPI } from '../../../../hooks/api';
import { getMembers } from '../../../../store/team/selectors';
import { INDEX_TO_COLOR } from '../../../../data';
import { TeamTableHeader } from './TeamTableHeader';

const TeamTable = () => {
  const { personnel } = useAppSelector(getAuthenticateUser);
  const apiRef = useGridApiRef() as MutableRefObject<GridApiPro>;
  const dispatch = useAppDispatch();
  const {
    localText, handleScrollTable, sortRows, filterRows, handleExport, exporting,
  } = useTable(apiRef);
  const { teamPermissions } = useTeamPermissions();
  const { permissions } = usePermissions();
  const isPrintMode = useAppSelector(getIsPrintMode);
  const componentRef = useRef<HTMLDivElement | null>(null);

  const theme = useTheme() as TCustomTheme;
  const { isMobile } = useCustomTheme(theme);
  const teamMembers = useAppSelector(getMembers);

  useGetTeamAPI(true);

  const { getTeamByFilters } = useTeamFilters();
  const { locationsByPermissions } = useCommon();
  const { t } = useTranslation(['inputs', 'common', 'team', 'orders']);

  const { pageHeight, resizableDiv, isDesktopSmallScreen } = useScreenResize(59);

  const [rows, setRows] = useState<TMember[]>([]);
  const [rowCount, setRowCount] = useState<number>(0);
  const [filterModel, setFilterModel] = useState<GridFilterModel>({} as GridFilterModel);
  const [sortModel, setSortModel] = useState<GridSortModel>([]);

  const [isFullTable, setIsFullTable] = useState(false);

  const MemoizedMemberStatus = memo(MemberStatus);
  const getLengthByStatus = (locationId: number) => teamMembers.reduce(
    (count, item) => (item.locations.some(({ id }) => id === locationId) ? count + 1 : count),
    0,
  );

  const tabs = useMemo(() => {
    const baseTabs = locationsByPermissions?.map((location, index) => ({
      value: location.id,
      label: location.name,
      color: INDEX_TO_COLOR[index],
      count: getLengthByStatus(location.id),
    }));

    baseTabs?.unshift({
      value: 0,
      label: t('all', { ns: 'common' }),
      color: 'info',
      count: teamMembers.length,
    });

    return baseTabs;
  }, [teamMembers, locationsByPermissions]);

  const onCellDoubleClick = (params: GridCellParams) => {
    dispatch(memberActions.setSelectedMember(params.row));

    setTimeout(() => {
      dispatch(commonActions.setIsOpenPagesNav(true));
    }, 100);
  };

  const handleFilterStatus = (newValue: number) => {
    dispatch(memberActions.setSelectedLocationId(newValue));
  };

  const renderCustomNoRowsOverlay = () => <CustomNoRowsOverlay text={t('itLooksLikeThereAreNoData', { ns: 'common' })} />;

  const fetchRows = useCallback(
    async (params: GridFetchRowsParams) => {
      const totalRows = getTeamByFilters.length;
      const firstRowToRender = params.firstRowToRender || 0;
      const lastRowToRender = Math.min(params.lastRowToRender || 10, totalRows);

      let filteredRows = [...getTeamByFilters];

      const quickFilterValues = params.filterModel?.quickFilterValues || [];

      if (params.sortModel && params.sortModel.length > 0) {
        filteredRows = sortRows(filteredRows, params.sortModel);
      }

      if (quickFilterValues.length > 0) {
        filteredRows = getTeamByFilters.filter((member: TMember) => quickFilterValues.some((value) => {
          const searchValue = value.toLowerCase();
          return (
            String(member.shopifyId).toLowerCase().includes(searchValue)
              || member.name.toLowerCase().includes(searchValue)
              || member.email.toLowerCase().includes(searchValue)
          );
        }));
      }

      if (params.filterModel && params.filterModel?.items?.length > 0) {
        filteredRows = filterRows(filteredRows, params.filterModel);
      }

      const slice = filteredRows.slice(firstRowToRender, lastRowToRender);

      return { slice, total: filteredRows.length };
    },
    [getTeamByFilters],
  );

  useEffect(() => {
    if (getTeamByFilters?.length === 0) {
      setRows([]);
      setRowCount(0);
      return;
    }

    const fetchInitialRows = async () => {
      const { slice, total } = await fetchRows({
        firstRowToRender: 0,
        lastRowToRender: 10,
        sortModel,
        filterModel,
      });
      setRows(slice);
      setRowCount(total);
    };

    fetchInitialRows();
  }, [fetchRows, getTeamByFilters, sortModel, filterModel]);

  const handleFetchRows = useCallback(
    async (params: GridFetchRowsParams) => {
      const { slice, total } = await fetchRows(params);

      apiRef.current?.unstable_replaceRows(params.firstRowToRender, slice);
      setRowCount(total);
    },
    [apiRef, fetchRows],
  );

  const debouncedHandleFetchRows = useMemo(
    () => debounce(handleFetchRows, 200),
    [handleFetchRows],
  );

  const teamColumns: Array<GridColDef> = useMemo(() => [
    {
      field: 'name',
      headerName: t('name'),
      width: 200,
      renderCell: ({ row }) => (
        <Box display="flex" alignItems="center" height="100%">
          <MemoizedBaseAvatar
            statusTooltipText={t('pendingRegistration', { ns: 'team' })}
            status={row.status}
            avatarFile={row.avatarFile}
            avatarName={row.name}
          />
          <Typography variant="caption">
            {startCase(row.name)}
          </Typography>
        </Box>
      ),
    },
    {
      field: 'permissionLevel',
      headerName: t('permission'),
      renderCell: ({ row }) => (
        <Box display="flex" alignItems="center" height="100%">
          <Typography variant="caption">
            {t(`${row.permissionLevel?.toLowerCase()}`, { ns: 'common' })}
          </Typography>
        </Box>
      ),
    },
    {
      field: 'email',
      headerName: t('email'),
      width: 200,
      renderCell: ({ row }) => (
        <Box display="flex" alignItems="center" height="100%">
          <Typography variant="caption">
            {row.email}
          </Typography>
        </Box>
      ),
    },
    {
      field: 'commissionPlan',
      headerName: t('commissionPlan', { ns: 'orders' }),
      width: 130,
      renderCell: ({ row }) => (
        <Box display="flex" alignItems="center" height="100%">
          <Typography variant="caption">
            {row.commissionPlan?.name || '/'}
          </Typography>
        </Box>
      ),
      valueGetter: (value: TCommissionPlan) => value?.name || '',
    },
    {
      field: 'shopifyId',
      headerName: 'Shopify ID',
      width: 130,
      renderCell: ({ row }) => (
        <Box id="team-shopifyId" display="flex" alignItems="center" height="100%">
          <Typography variant="caption">
            {row.shopifyId || '/'}
          </Typography>
        </Box>
      ),
    },
    {
      field: 'pinCode',
      headerName: `${t('pinCode', { ns: 'team' })} (POS)`,
      width: 130,
      renderCell: ({ row }) => {
        const connectedUser = row.email === personnel.email;
        return (
          <Box id="team-pincode" display="flex" alignItems="center" height="100%">
            <Typography variant="caption">
              {teamPermissions.isAllowViewSalaryData || connectedUser ? (row.pinCode || '/') : '/'}
            </Typography>
          </Box>
        );
      },
    },
    {
      field: 'locations',
      headerName: t('locations'),
      minWidth: 130,
      renderCell: ({ row }) => (
        <Box display="flex" alignItems="center" height="100%">
          <MemoizedMultiLocations locations={row.locations} />
        </Box>
      ),
      valueGetter: (value: Array<TLocation>) => {
        if (Array.isArray(value)) {
          const locationNames = value?.map((location: TLocation) => location.name);

          return locationNames.join(' ,');
        }

        return value;
      },
    },
    {
      field: 'commissionTags',
      headerName: t('commissionTags', { ns: 'team' }),
      minWidth: 130,
      renderCell: ({ row }) => (
        row.commissionTags?.length
          ? row.commissionTags?.map((tag: string, index: number) => (
            <BaseChip
              color="#A855F7"
              key={`${tag}-${index.toString()}`}
              label={tag}
              size="small"
            />
          )) : (
            <Typography variant="caption">
              /
            </Typography>
          )
      ),
    },
    {
      field: 'department',
      headerName: t('department'),
      minWidth: 130,
      renderCell: ({ row }) => (
        (
          row.department
            ? <span className="department-label">{row.department}</span>
            : <span>/</span>
        )
      ),
    },
    {
      field: 'position',
      headerName: t('position'),
      minWidth: 130,
      renderCell: ({ row }) => (
        (
          row.position
            ? (
              <Box display="flex" alignItems="center" height="100%">
                <BaseLabel fontSize="11" letterSpacing="-1px">
                  {row.position}
                </BaseLabel>
              </Box>
            )
            : <span>/</span>
        )
      ),
    },
    {
      field: 'phone.e164',
      headerName: t('phone'),
      minWidth: 130,
      renderCell: ({ row }) => {
        const phone = typeof row.phone === 'string' ? row.phone : get(row, 'phone.e164', '/');

        return (
          <Box display="flex" alignItems="center" height="100%">
            <Typography variant="caption">
              {phone || '/'}
            </Typography>
          </Box>
        );
      },
      valueGetter: (_, row) => get(row, 'phone.e164', '/'),
    },
    {
      field: 'salaryWageString',
      headerName: t('wage'),
      minWidth: 130,
      renderCell: ({ row }) => (
        <Box display="flex" alignItems="center" height="100%">
          <Typography variant="caption">
            {get(row, 'salaryWageString', '/')}
          </Typography>
        </Box>
      ),
    },
    {
      field: 'timeoffLimit',
      headerName: t('timeoffLimit', { ns: 'team' }),
      minWidth: 130,
      renderCell: ({ row }) => {
        const timeoffLimit = row.timeoffLimit || '/';
        return (
          <Typography variant="caption">
            {timeoffLimit}
          </Typography>
        );
      },
    },
    {
      field: 'sickLeaveLimit',
      headerName: t('sickLeaveLimit', { ns: 'team' }),
      minWidth: 130,
      renderCell: ({ row }) => {
        const timeoffLimit = row.sickLeaveLimit || '/';

        return (
          <Typography variant="caption">
            {timeoffLimit}
          </Typography>
        );
      },
    },
    {
      field: 'status',
      headerName: t('status'),
      minWidth: 60,
      renderCell: ({ row }) => (
        <MemoizedMemberStatus email={row.email} status={row.status} />
      ),
    },
    {
      field: 'actions',
      type: 'actions',
      headerName: t('actions', { ns: 'common' }),
      disableExport: true,
      sortable: false,
      align: 'right',
      getActions: ({ row }) => {
        const openDeleteDialog = () => {
          dispatch(memberActions.toggleDeleteDialog(true));
          dispatch(memberActions.setSelectedMember(row));
        };

        const openTeamNav = () => {
          dispatch(memberActions.setSelectedMember(row));

          setTimeout(() => {
            dispatch(commonActions.setIsOpenPagesNav(true));
          }, 100);
        };

        const dynamicIcon = (
          (!teamPermissions.isAllowManage || permissions.isEmployee)
            && row.email === personnel.email ? (
              <SvgIcon onClick={openTeamNav} component={ViewIcon} fontSize="small" />
            ) : null
        );

        return TableActions({
          onConfirm: teamPermissions.isAllowManage && !permissions.isEmployee ? openTeamNav : undefined,
          onDelete: teamPermissions.isAllowManage && !permissions.isEmployee && row.email !== personnel.email
            ? openDeleteDialog : undefined,
          dynamicIcon,
        });
      },
    },
  ], [t, personnel, teamPermissions]);

  const renderTableHeader = useCallback(() => (
    <TeamTableHeader
      handleScrollTable={handleScrollTable}
      printRef={componentRef}
      apiRef={apiRef}
      isFullTable={isFullTable}
      setIsFullTable={setIsFullTable}
      handleExport={() => handleExport('mgm-team')}
    />
  ), [apiRef, isFullTable, getTeamByFilters]);

  return (
    <PrintWrapper componentRef={componentRef}>
      <div ref={resizableDiv}>
        <Card
          sx={{
            // eslint-disable-next-line no-nested-ternary
            height: exporting ? 'auto' : (isDesktopSmallScreen
              ? 'auto'
            // eslint-disable-next-line no-nested-ternary
              : isFullTable && rows?.length > 0
                ? 'auto'
                : pageHeight !== 0 && !isPrintMode
                  ? pageHeight
                  : 'auto'),
            pb: 6,
            ...(isMobile && {
              height: 'auto',
              background: 'unset',
              boxShadow: 'unset',
              px: 0,
              pt: 0,
              pb: '30px',
            }),
          }}
        >
          <div id="team-locations">
            <MemoizedFilterTabs isDisplayBackground={!isMobile} tabs={tabs} onFilterChange={handleFilterStatus} />
          </div>
          <DataGridPro
            apiRef={apiRef}
            rows={rows}
            localeText={localText}
            slots={{
              toolbar: renderTableHeader as any,
              loadingOverlay: LinearProgress as any,
              noRowsOverlay: renderCustomNoRowsOverlay,
            }}
            sx={{
              px: 0,
              '.MuiDataGrid-main > *:first-of-type': {
                borderTopLeftRadius: 0,
                borderTopRightRadius: 0,
              },
            }}
            slotProps={{
              toolbar: {
                showQuickFilter: true,
                printOptions: {
                  hideFooter: true,
                  hideToolbar: true,
                },
              },
            }}
            {...(!isMobile && {
              initialState: { pinnedColumns: { left: ['name'], right: ['actions'] } },
            })}
            showToolbar
            getRowId={(row) => row.email}
            columns={teamColumns}
            hideFooterPagination
            disableRowSelectionOnClick
            hideFooter
            onCellDoubleClick={onCellDoubleClick}
            rowCount={rowCount}
            sortingMode="server"
            filterMode="server"
            rowsLoadingMode="server"
            checkboxSelection={false}
            sortModel={sortModel}
            onSortModelChange={setSortModel}
            className={isPrintMode ? 'print-mode' : ''}
            onFilterModelChange={setFilterModel}
            onFetchRows={debouncedHandleFetchRows}
          />
        </Card>
      </div>
    </PrintWrapper>
  );
};

export {
  TeamTable,
};
